<template>
  <div class="wizard-body w-[75%] flex-shrink-0 flex-grow-0 max-w-[75%]">
    <div v-if="!hasLoginStarted" class="login-init h-full">
      <a href="#" @click="startLogin()">
        <img
          src="~dashboard/assets/images/channels/zalo_login.jpg"
          alt="Zalo-logo"
        />
      </a>
      <p>
        {{
          useInstallationName(
            $t('INBOX_MGMT.ADD.ZALO.HELP'),
            globalConfig.installationName
          )
        }}
      </p>
    </div>
    <div v-else>
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <loading-state v-else-if="showLoader" :message="emptyStateMessage" />
      <form
        v-else
        class="mx-0 flex flex-wrap"
        @submit.prevent="createChannel()"
      >
        <div class="w-full">
          <page-header
            :header-title="$t('INBOX_MGMT.DETAILS.TITLE')"
            :header-content="
              useInstallationName(
                $t('INBOX_MGMT.DETAILS.DESC_ZALO'),
                globalConfig.installationName
              )
            "
          />
        </div>
        <div class="w-[60%]">
          <div class="w-full">
            <label :class="{ error: v$.pageName.$error }">
              {{ $t('INBOX_MGMT.ADD.ZALO.INBOX_NAME') }}
              <input
                v-model.trim="pageName"
                type="text"
                :placeholder="$t('INBOX_MGMT.ADD.ZALO.PICK_NAME')"
                @input="v$.pageName.$touch"
              />
              <span v-if="v$.pageName.$error" class="message">
                {{ $t('INBOX_MGMT.ADD.ZALO.ADD_NAME') }}
              </span>
            </label>
          </div>
          <div class="w-full text-right">
            <input type="submit" value="Create Inbox" class="button" :disabled="isCreating || $store.getters['inboxes/getUIFlags'].isCreating" />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
<script>
/* eslint-env browser */
import { required } from '@vuelidate/validators';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { mapGetters } from 'vuex';
import axios from 'axios';
import PageHeader from '../../SettingsSubPageHeader.vue';
import router from '../../../../index';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import ZaloOaChannel from '../../../../../api/channel/zaloOaChannel';
import { useVuelidate } from '@vuelidate/core';
import { useAccount } from 'dashboard/composables/useAccount';


export default {
  components: {
    LoadingState,
    PageHeader,
  },
  mixins: [globalConfigMixin],
  setup() {
    const { accountId } = useAccount();
    return {
      accountId,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isCreating: false,
      hasError: false,
      omniauth_token: '',
      access_token: '',
      refresh_token: '',
      expires_in: '',
      oa_id: '',
      pageName: '',
      avatar: '',
      channel: 'zalooa',
      emptyStateMessage: '',
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
    };
  },

  validations: {
    pageName: {
      required,
    },
    selectedPage: {
      isEmpty() {
        return this.selectedPage !== null && !!this.selectedPage.name;
      },
    },
  },

  computed: {
    showLoader() {
      return this.oa_id === '' || this.isCreating;
    },
    ...mapGetters({
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
    }),
  },

  created() {
    if (this.$route.query.code) this.getAccessToken();
  },

  methods: {
    startLogin() {
      this.hasLoginStarted = true;
      
      // Debug logging
      console.log('Zalo OAuth Configuration:', {
        zaloAppId: window.hichatConfig.zaloAppId,
        hostURL: window.hichatConfig.hostURL,
        accountId: this.accountId
      });
      
      // Validate required configs
      if (!window.hichatConfig.zaloAppId) {
        this.hasError = true;
        this.errorStateMessage = 'Zalo App ID not configured';
        return;
      }
      
      if (!window.hichatConfig.hostURL) {
        this.hasError = true;
        this.errorStateMessage = 'Host URL not configured';
        return;
      }
      
      if (!this.accountId) {
        this.hasError = true;
        this.errorStateMessage = 'Account ID not available';
        return;
      }
      
      const oauthUrl = 'https://oauth.zaloapp.com/v4/oa/permission?' + 
        'app_id=' + encodeURIComponent(window.hichatConfig.zaloAppId) +
        '&redirect_uri=' + encodeURIComponent(window.hichatConfig.hostURL + '/zalooa/callback') +
        '&state=' + encodeURIComponent(this.accountId);
        
      console.log('Redirecting to Zalo OAuth URL:', oauthUrl);
      window.location.replace(oauthUrl);
    },

    async getSecretKey() {
      const response = await ZaloOaChannel.secretKey();
      return response.data.secret_key;
    },

    setPageName({ name }) {
      this.v$.selectedPage.$touch();
      this.pageName = name;
    },

    async getAccessToken() {
      this.hasLoginStarted = true;
      
      console.log('Getting access token with code:', this.$route.query.code);
      
      if (!this.$route.query.code) {
        this.hasError = true;
        this.errorStateMessage = 'No authorization code received from Zalo OA';
        return;
      }
      
      const url = 'https://oauth.zaloapp.com/v4/oa/access_token';
      const data = new URLSearchParams();
      data.append('code', this.$route.query.code);
      data.append('app_id', window.hichatConfig.zaloAppId);
      data.append('grant_type', 'authorization_code');
      
      try {
        const secret_key = await this.getSecretKey();
        
        const config = {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            secret_key: secret_key,
          },
        };

        const response = await axios.post(url, data, config);
        
        console.log('Zalo access token response:', response.data);
        
        if (response.data.error && response.data.error !== 0) {
          this.hasError = true;
          this.errorStateMessage = response.data.message || 'Failed to get access token';
          return;
        }
        
        this.access_token = response.data.access_token;
        this.refresh_token = response.data.refresh_token;
        this.expires_in = response.data.expires_in;
        this.fetchOaData(this.access_token);
        
      } catch (error) {
        console.error('Error getting access token:', error);
        this.hasError = true;
        this.errorStateMessage = error.response?.data?.message || error.message || 'Failed to exchange authorization code';
      }
    },

    fetchOaData(access_token) {
      const url = 'https://openapi.zalo.me/v2.0/oa/getoa';

      axios
        .get(url, {
          headers: {
            access_token: access_token,
          },
        })
        .then(response => {
          const data = response.data;
          if (data.error === 0) {
            this.oa_id = data.data.oa_id;
            this.pageName = data.data.name;
            this.avatar = data.data.avatar;
          } else {
            this.hasError = true;
            this.errorStateMessage = data.message;
          }
        })
        .catch(error => {
          this.hasError = true;
          this.errorStateMessage = error.message;
        });
    },

    channelParams() {
      return {
        oa_id: this.oa_id,
        access_token: this.access_token,
        refresh_token: this.refresh_token,
        expires_in: this.expires_in,
        inbox_name: this.pageName || '',
        avatar_url: this.avatar,
      };
    },

    createChannel() {
      this.v$.$touch();
      this.emptyStateMessage = '';
      this.isCreating = true;
      this.hasError = false;
      this.$store
        .dispatch('inboxes/createZaloOaChannel', this.channelParams())
        .then(data => {
          router.replace({
            name: 'settings_inboxes_add_agents',
            params: { page: 'new', inbox_id: data.id },
          });
        })
        .catch(() => {
          this.isCreating = false;
        });
    },
  },
};
</script>
